import { useState, useCallback, useRef, useEffect } from 'react';
import L from 'leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
  faShareNodes,
  faQrcode,
} from '@fortawesome/pro-regular-svg-icons';
import { faRoute } from '@fortawesome/pro-solid-svg-icons';

import {
  useAppSelector,
  useAppDispatch,
  mapSelector,
  activeImageSelector,
  activeSpaceSelector,
  setActiveSpace,
  setCustomRouteStart,
  setCustomRouteEnd,
  setShowCustomRoute,
  SpaceType,
} from '@/store';

import eventBus from '@/eventBus';
import { interactiveURL } from "@/config";
import { QrCodeModal } from '@/components/Map/PathOverlay/QrCodeModal';

import './SpaceCard.scss';

interface SpaceCardProps {
  /** Callback when the View/Tour button is clicked */
  onOpenModal: () => void;
  connectedSpaces: SpaceType[];
  showNavigation: boolean;
}

/**
 * An elegant card-style overlay for displaying space details.
 * Used when popupStyle preference is set to 'card'.
 */
export function SpaceCard({ onOpenModal, connectedSpaces, showNavigation }: SpaceCardProps) {
  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const space = useAppSelector(activeSpaceSelector);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const spaceLink = `${interactiveURL}/${iMap.order.id}/map/${iMap.id}/layer/${activeImage?.id}/space/${space?.id}`;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    // Prevent Leaflet from zooming via mouse wheel (desktop)
    L.DomEvent.disableScrollPropagation(el);
    // Prevent Leaflet from panning via touch (mobile).
    L.DomEvent.on(el, 'touchstart touchmove', L.DomEvent.stopPropagation);
    return () => {
      L.DomEvent.off(el, 'touchstart touchmove', L.DomEvent.stopPropagation);
    };
  }, []);

  const handleClose = useCallback(() => {
    dispatch(setActiveSpace(null));
  }, [dispatch]);

  const copyLinkToClipboard = useCallback(() => {
    navigator.clipboard.writeText(spaceLink);
    eventBus.emit('flash', 'success', 'Link copied to clipboard');
  }, [spaceLink]);

  //HACK replace thumb by a higher resolution image, on SpaceCard thumbs become blurry
  const visual = space?.visual?.includes('/preview_items/images/images/')
    ? space?.visual?.replace('/thumb/', '/lg/')
    : space?.visual?.includes('/interactive/spaces/visual_uploadeds/')
      ? space?.visual?.replace('/thumb/', '/original/')
      : space?.visual;

  // Get carousel images or fall back to main visual
  const images = space?.carousel_images?.length
    ? space.carousel_images.map((img) => img.url || img.thumb)
    : visual
      ? [visual]
      : space?.visual_uploaded?.url
        ? [space.visual_uploaded.url]
        : [];

  const hasMultipleImages = images.length > 1;

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const hasMedia = !!(space?.vr_iframe || space?.vr_model || space?.video || space?.carousel_images?.length);

  const handleNavigateTo = useCallback((toSpaceId: string) => {
    if (!space) return;
    dispatch(setCustomRouteStart(space.id));
    dispatch(setCustomRouteEnd(toSpaceId));
    dispatch(setShowCustomRoute(true));
    dispatch(setActiveSpace(null));
  }, [dispatch, space]);

  if (!space) return null;

  return (
    <>
      <div className="space-card-overlay">
        <div ref={cardRef} className="space-card" onClick={(e) => e.stopPropagation()}>
          <button
            className="space-card__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="space-card__content">
            {/* Image carousel / media section */}
            {images.length > 0 && (
              <div className="space-card__media">
                <img
                  src={images[currentImageIndex]}
                  alt={space.name}
                  className="space-card__image"
                />

                {hasMultipleImages && (
                  <>
                    <button
                      className="space-card__nav space-card__nav--prev"
                      onClick={prevImage}
                      aria-label="Previous image"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="space-card__nav space-card__nav--next"
                      onClick={nextImage}
                      aria-label="Next image"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Info section */}
            <div className="space-card__info">
              <div className="space-card__header">
                <button
                  className="space-card__qr"
                  onClick={() => setShowQrModal(true)}
                  aria-label="QR Code"
                  title="Show QR code for this space"
                >
                  <FontAwesomeIcon icon={faQrcode} />
                </button>
                <button
                  className="space-card__share"
                  onClick={() => copyLinkToClipboard()}
                  aria-label="Share"
                  title="Copy link to clipboard"
                >
                  <FontAwesomeIcon icon={faShareNodes} />
                </button>
              </div>

              <h2 className="space-card__title">{space.name}</h2>

              {space.subtitle && (
                <p className="space-card__subtitle">{space.subtitle}</p>
              )}

              {space.description && (
                <p className="space-card__description" dangerouslySetInnerHTML={{ __html: space.description }} />
              )}

              {showNavigation && connectedSpaces.length > 0 && (
                <div className="space-card__navigation">
                  <label className="navigate-label">
                    <FontAwesomeIcon icon={faRoute} />
                    Navigate to:
                  </label>
                  <select
                    className="navigate-select"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleNavigateTo(e.target.value);
                      }
                    }}
                  >
                    <option value="" disabled>Select destination...</option>
                    {connectedSpaces.map((connSpace) => (
                      <option key={connSpace.id} value={connSpace.id}>
                        {connSpace.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-card__actions">
                {hasMedia && (
                  <button
                    className="space-card__btn space-card__btn--primary"
                    onClick={() => {
                      onOpenModal();
                    }}
                  >
                    View
                  </button>
                )}

                {space.external_link && (
                  <a
                    href={space.external_link}
                    target="_blank"
                    rel="noreferrer"
                    className="space-card__btn space-card__btn--secondary"
                  >
                    Learn More
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
          </div>
      {showQrModal && (
          <QrCodeModal
            waypointUrl={spaceLink}
            waypointName={space.name}
            onClose={() => setShowQrModal(false)}
          />
        )}
    </>
  );
}

export default SpaceCard;
